import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDate, prettify } from "@/lib/format";
import { REFERRAL_STATUSES, TONE, optOf, labelOf, partnerTypeLabel } from "@/config/partnerOptions";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PopoutDialog } from "@/components/ui/PopoutDialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { GitPullRequestArrow, Plus, Search, Handshake, FolderHeart } from "lucide-react";
import { toast } from "sonner";

const StatusPill = ({ value }) => {
  const o = optOf(REFERRAL_STATUSES, value); const t = TONE[o?.tone || "gray"];
  return <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold border whitespace-nowrap" style={{ backgroundColor: t.bg, color: t.color, borderColor: t.border }} data-testid="referral-status-badge">{o?.label || labelOf(REFERRAL_STATUSES, value)}</span>;
};

export default function Referrals() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canWrite = Array.isArray(user?.writable_resources) && user.writable_resources.includes("partners");

  const [items, setItems] = useState([]);
  const [partners, setPartners] = useState([]);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ q: "", status: "all", partner_id: "all" });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ partner_id: "", case_id: "", referral_type: "", status: "sent", notes: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.q) params.q = filters.q;
      if (filters.status !== "all") params.status = filters.status;
      if (filters.partner_id !== "all") params.partner_id = filters.partner_id;
      const { data } = await api.get("/referrals", { params });
      setItems(data.items || []);
    } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => {
    api.get("/partners", { params: { limit: 500 } }).then((r) => setPartners(r.data.items || [])).catch(() => {});
    api.get("/cases", { params: { limit: 200 } }).then((r) => setCases(r.data.items || [])).catch(() => {});
  }, []);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (!form.partner_id) { toast.error("Choose a partner"); return; }
    setBusy(true);
    try { await api.post("/referrals", form); toast.success("Referral created"); setOpen(false); setForm({ partner_id: "", case_id: "", referral_type: "", status: "sent", notes: "" }); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not create referral"); } finally { setBusy(false); }
  };
  const updateStatus = async (rid, status) => {
    try { await api.put(`/referrals/${rid}`, { status }); setItems((prev) => prev.map((r) => (r.id === rid ? { ...r, status } : r))); toast.success("Referral updated"); }
    catch { toast.error("Could not update"); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h2 className="font-display text-2xl font-bold text-[#252221] flex items-center gap-2"><GitPullRequestArrow size={22} className="text-[#1C4358]" /> Referrals</h2>
          <p className="text-sm text-muted-foreground mt-0.5">Track every partner referral from sent to revenue received.</p>
        </div>
        {canWrite && <Button onClick={() => setOpen(true)} data-testid="new-referral-button" className="text-white" style={{ backgroundColor: "var(--mmm-red)" }}><Plus size={17} className="mr-1.5" /> New Referral</Button>}
      </div>

      <Card className="p-3 mb-4 border-[#E6EDF2]" style={{ boxShadow: "var(--mmm-shadow-sm)" }}>
        <div className="flex flex-col lg:flex-row lg:items-center gap-2">
          <div className="relative flex-1 min-w-[180px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={filters.q} onChange={(e) => setFilters((p) => ({ ...p, q: e.target.value }))} placeholder="Search partner, case, notes..." className="pl-9 h-9" data-testid="referral-search" />
          </div>
          <div className="grid grid-cols-2 lg:flex gap-2">
            <Select value={filters.status} onValueChange={(v) => setFilters((p) => ({ ...p, status: v }))}>
              <SelectTrigger className="h-9 w-full sm:w-[160px]" data-testid="filter-status"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All statuses</SelectItem>{REFERRAL_STATUSES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={filters.partner_id} onValueChange={(v) => setFilters((p) => ({ ...p, partner_id: v }))}>
              <SelectTrigger className="h-9 w-full sm:w-[180px]" data-testid="filter-partner"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-[300px]"><SelectItem value="all">All partners</SelectItem>{partners.map((p) => <SelectItem key={p.id} value={p.id}>{p.display_name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      <div className="rounded-xl border border-[#E6EDF2] bg-white overflow-hidden" style={{ boxShadow: "var(--mmm-shadow-sm)" }}>
        {loading ? (
          <div className="p-4 space-y-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
        ) : items.length === 0 ? (
          <div className="py-16 text-center">
            <div className="mx-auto mb-3 h-14 w-14 rounded-2xl flex items-center justify-center" style={{ backgroundColor: "rgba(28,67,88,0.08)" }}><GitPullRequestArrow size={26} className="text-[#1C4358]" /></div>
            <p className="font-medium text-[#252221]">No referrals yet</p>
            <p className="text-sm text-muted-foreground">Create a referral to connect a family case with a partner.</p>
          </div>
        ) : (
          <div className="overflow-x-auto mmm-scroll">
            <Table data-testid="referrals-table">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="text-xs font-semibold text-[#1C4358]/80">Partner</TableHead>
                  <TableHead className="text-xs font-semibold text-[#1C4358]/80">Family Case</TableHead>
                  <TableHead className="text-xs font-semibold text-[#1C4358]/80">Type</TableHead>
                  <TableHead className="text-xs font-semibold text-[#1C4358]/80">Sent</TableHead>
                  <TableHead className="text-xs font-semibold text-[#1C4358]/80">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((r) => (
                  <TableRow key={r.id} className="hover:bg-[rgba(155,185,206,0.10)]" data-testid="referral-row">
                    <TableCell className="py-3">
                      <button className="font-semibold text-[#252221] hover:text-[#1C4358] flex items-center gap-1.5 text-left" onClick={() => r.partner_id && navigate(`/partners/${r.partner_id}`)} data-testid="referral-partner-link">
                        <Handshake size={14} className="text-[#1C4358]" /> {r.partner_name || "Partner"}
                      </button>
                      {r.partner_type && <div className="text-xs text-muted-foreground ml-5">{partnerTypeLabel(r.partner_type)}</div>}
                    </TableCell>
                    <TableCell>
                      {r.case_id ? (
                        <button className="text-sm text-[#252221] hover:text-[#1C4358] flex items-center gap-1.5" onClick={() => navigate(`/cases/${r.case_id}`)} data-testid="referral-case-link"><FolderHeart size={13} /> {r.case_name || "Case"}</button>
                      ) : <span className="text-sm text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{r.referral_type ? prettify(r.referral_type) : "—"}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{r.sent_date ? formatDate(r.sent_date) : formatDate(r.created_at)}</TableCell>
                    <TableCell>
                      {canWrite ? (
                        <Select value={r.status || ""} onValueChange={(v) => updateStatus(r.id, v)}>
                          <SelectTrigger className="h-8 w-[170px]" data-testid="referral-row-status"><SelectValue /></SelectTrigger>
                          <SelectContent>{REFERRAL_STATUSES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                        </Select>
                      ) : <StatusPill value={r.status} />}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
      <p className="text-xs text-muted-foreground mt-2">{items.length} referral{items.length === 1 ? "" : "s"}</p>

      <PopoutDialog
        open={open}
        onOpenChange={setOpen}
        title="New Referral"
        subtitle="Referrals"
        icon={GitPullRequestArrow}
        maxWidthClass="max-w-md"
        testid="new-referral-dialog"
      >
        <form onSubmit={create} className="space-y-3">
          <p className="text-sm text-muted-foreground -mt-1">Connect a family case with a partner or vendor.</p>
            <div><Label className="mb-1.5 block text-[13px]">Partner *</Label>
              <Select value={form.partner_id || ""} onValueChange={(v) => setForm((p) => ({ ...p, partner_id: v }))}>
                <SelectTrigger data-testid="new-referral-partner"><SelectValue placeholder="Choose partner" /></SelectTrigger>
                <SelectContent className="max-h-[260px]">{partners.map((p) => <SelectItem key={p.id} value={p.id}>{p.display_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="mb-1.5 block text-[13px]">Family Case</Label>
              <Select value={form.case_id || ""} onValueChange={(v) => setForm((p) => ({ ...p, case_id: v }))}>
                <SelectTrigger data-testid="new-referral-case"><SelectValue placeholder="Choose case (optional)" /></SelectTrigger>
                <SelectContent className="max-h-[260px]">{cases.map((c) => <SelectItem key={c.id} value={c.id}>{c.case_name || c.family_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="mb-1.5 block text-[13px]">Referral type</Label><Input value={form.referral_type} onChange={(e) => setForm((p) => ({ ...p, referral_type: e.target.value }))} placeholder="e.g. Senior placement, Home sale" data-testid="new-referral-type" /></div>
            <div><Label className="mb-1.5 block text-[13px]">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm((p) => ({ ...p, status: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{REFERRAL_STATUSES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="mb-1.5 block text-[13px]">Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} data-testid="new-referral-notes" /></div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={busy} className="text-white" style={{ backgroundColor: "var(--mmm-red)" }} data-testid="create-referral-button">{busy ? "Creating..." : "Create Referral"}</Button>
            </div>
        </form>
      </PopoutDialog>
    </div>
  );
}
